import React, { createContext, useState, useEffect, useContext } from 'react';
import { notificationApi } from '../api/notificationApi';
import { SocketContext } from './SocketContext';
import { AuthContext } from './AuthContext';
import toast from 'react-hot-toast';

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { socket } = useContext(SocketContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = async () => {
    if (!user) return;
    try {
      const res = await notificationApi.getNotifications();
      setNotifications(res?.data?.notifications || res?.notifications || []);
      setUnreadCount(res?.data?.unreadCount ?? res?.unreadCount ?? 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [user]);

  useEffect(() => {
    if (socket) {
      socket.on('new_notification', (data) => {
        toast.custom(
          (t) => (
            <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-slate-900 shadow-xl rounded-xl pointer-events-auto flex ring-1 ring-green-600/30 p-4`}>
              <div className="flex-1 w-0">
                <p className="text-sm font-semibold text-green-500">{data.title}</p>
                <p className="mt-1 text-xs text-slate-300">{data.message}</p>
              </div>
            </div>
          ),
          { duration: 4000 }
        );
        fetchNotifications();
      });

      socket.on('receive_message', (data) => {
        if (data.senderId !== user?._id) {
          toast(`New message from ${data.senderModel === 'Admin' ? 'Admin' : 'SuperAdmin'}`, {
            icon: '💬',
          });
        }
      });

      return () => {
        socket.off('new_notification');
        socket.off('receive_message');
      };
    }
  }, [socket, user]);

  const markAsRead = async (id) => {
    try {
      await notificationApi.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error(err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        fetchNotifications,
        markAsRead,
        markAllAsRead
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
